import { apiClient, authFetchBlob, orgApiPath } from '@/core/api';
import { auth } from '@/core/firebase';
import type { Asset } from '@/types/api';

function xhrErrorMessage(xhr: XMLHttpRequest): string {
  const text = xhr.responseText?.trim() ?? '';
  const ct = xhr.getResponseHeader('content-type') ?? '';
  if (ct.includes('application/json') && text) {
    try {
      const body = JSON.parse(text) as { detail?: unknown };
      const d = body.detail;
      if (d == null) return text || `API error ${xhr.status}`;
      if (typeof d === 'string') return d;
      if (Array.isArray(d)) {
        return d
          .map((item) => {
            if (item && typeof item === 'object' && 'msg' in item) {
              return String((item as { msg: unknown }).msg);
            }
            return JSON.stringify(item);
          })
          .filter(Boolean)
          .join('; ');
      }
      if (typeof d === 'object') return JSON.stringify(d);
      return String(d);
    } catch {
      return text || `API error ${xhr.status}`;
    }
  }
  return text || `API error ${xhr.status}: ${xhr.statusText}`;
}

export async function listAssets(offset = 0, limit = 50): Promise<Asset[]> {
  return apiClient<Asset[]>(orgApiPath(`/assets?offset=${offset}&limit=${limit}`));
}

export async function getAsset(id: number): Promise<Asset> {
  return apiClient<Asset>(orgApiPath(`/assets/${id}`));
}

export type AssetUploadTransport = 'multipart' | 'resumable';

export interface AssetUploadConfig {
  transport: AssetUploadTransport;
}

export async function getAssetUploadConfig(): Promise<AssetUploadConfig> {
  return apiClient<AssetUploadConfig>(orgApiPath('/assets/upload-config'));
}

export interface UploadAssetParams {
  file: File;
  name?: string;
  alt_text?: string;
}

interface ResumableSessionResponse {
  upload_url: string;
  asset_id: number;
  relative_uri: string;
}

function multipartUploadWithProgress(
  params: UploadAssetParams,
  onProgress?: (progress: number) => void,
): Promise<Asset> {
  return new Promise((resolve, reject) => {
    void (async () => {
      const user = auth.currentUser;
      if (!user) {
        reject(new Error('Not authenticated'));
        return;
      }
      let token: string;
      try {
        token = await user.getIdToken();
      } catch (e) {
        reject(e instanceof Error ? e : new Error('Failed to get ID token'));
        return;
      }

      const path = orgApiPath('/assets/upload');
      const url = `/api/v1${path}`;

      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      xhr.timeout = 600_000;

      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable && onProgress) {
          onProgress(Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            resolve(JSON.parse(xhr.responseText) as Asset);
          } catch {
            reject(new Error('Invalid JSON from upload response'));
          }
        } else {
          reject(new Error(xhrErrorMessage(xhr)));
        }
      };
      xhr.onerror = () => reject(new Error('Network error'));
      xhr.ontimeout = () => reject(new Error('Upload timed out'));

      const form = new FormData();
      form.append('file', params.file);
      if (params.name != null && params.name.trim() !== '') {
        form.append('name', params.name.trim());
      }
      if (params.alt_text != null && params.alt_text.trim() !== '') {
        form.append('alt_text', params.alt_text.trim());
      }
      xhr.send(form);
    })();
  });
}

function putFileToResumableUrl(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    const ct = file.type && file.type.trim() !== '' ? file.type : 'application/octet-stream';
    xhr.setRequestHeader('Content-Type', ct);
    xhr.timeout = 600_000;
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable && onProgress) {
        onProgress(Math.min(100, Math.round((ev.loaded / ev.total) * 100)));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(xhrErrorMessage(xhr)));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during direct upload'));
    xhr.ontimeout = () => reject(new Error('Direct upload timed out'));
    xhr.send(file);
  });
}

async function resumableUploadWithProgress(
  params: UploadAssetParams,
  onProgress?: (progress: number) => void,
): Promise<Asset> {
  const file = params.file;
  const contentType = file.type && file.type.trim() !== '' ? file.type : 'application/octet-stream';
  const session = await apiClient<ResumableSessionResponse>(
    orgApiPath('/assets/resumable-session'),
    {
      method: 'POST',
      body: JSON.stringify({
        filename: file.name,
        content_type: contentType,
        name: params.name != null && params.name.trim() !== '' ? params.name.trim() : null,
        alt_text: params.alt_text?.trim() ?? '',
      }),
    },
  );
  await putFileToResumableUrl(session.upload_url, file, onProgress);
  onProgress?.(100);
  return apiClient<Asset>(orgApiPath('/assets/resumable-finalize'), {
    method: 'POST',
    body: JSON.stringify({
      asset_id: session.asset_id,
      filename: file.name,
      content_type: contentType,
      name: params.name != null && params.name.trim() !== '' ? params.name.trim() : null,
      alt_text: params.alt_text?.trim() ?? '',
    }),
  });
}

export function uploadAssetWithProgress(
  params: UploadAssetParams,
  onProgress?: (progress: number) => void,
): Promise<Asset> {
  return (async () => {
    const { transport } = await getAssetUploadConfig();
    if (transport === 'resumable') {
      return resumableUploadWithProgress(params, onProgress);
    }
    return multipartUploadWithProgress(params, onProgress);
  })();
}

export async function uploadAsset(params: UploadAssetParams): Promise<Asset> {
  return uploadAssetWithProgress(params);
}

export async function downloadAssetContentFile(assetId: number, filename: string): Promise<void> {
  const blob = await authFetchBlob(orgApiPath(`/assets/${assetId}/content`));
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
